import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import { CommonActions, useFocusEffect } from '@react-navigation/native';
import color from 'color';
import type { FC, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { ViewStyle } from 'react-native';
import { BackHandler, StyleSheet, View } from 'react-native';
import type { TextProps } from 'react-native-paper';
import { Appbar, Badge } from 'react-native-paper';

import { CloseIcon, ArrowBackIcon } from '@/assets';
import type { ProfileAvatarProps } from '@/features/media/components/avatar/ProfileAvatar';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { getSizedMediaItemUrl } from '@/features/media/utils/mediaItem';
import { NavigationBarDefaultTitle } from '@/navigation/components/navbar/NavigationBarTitle';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { NavBarType } from '@/navigation/types/navbar';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { hasItems } from '@/shared/utils/array';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { SVGComponent } from '@/types';

type NavigationBarProps = {
  type?: NavBarType;
  direction?: ViewStyle['flexDirection'];
  title?: string;
  titleComponent?: ReactNode;
  titleVariant?: TextProps<string>['variant'];
  titleColor?: string;
  subTitle?: ReactNode;
  subTitleVariant?: TextProps<string>['variant'];
  subTitleColor?: string;
  centerTitle?: boolean;
  onTitlePress?: () => void;
  hasBackButton?: boolean;
  disableBackButton?: boolean;
  /**
   * Will also override the hardware back button
   */
  onBackButtonPress?: () => void;
  iconColor?: string;
  backgroundColor?: string;
  picture?: ClientMediaItem;
  pictureFallback?: {
    localPicture?: ProfileAvatarProps['localImageUri'];
    text?: ProfileAvatarProps['textFallback'];
    icon?: ProfileAvatarProps['iconFallback'];
  };
  /**
   * If not passed, gallery with profile picture will be opened
   */
  onPicturePress?: () => void;
  /**
   * - Endbuttons with types DEFAULT, CENTERED and MODAL_WITH_BACK
   * - Startbuttons with MODAL_WITH_CLOSE
   */
  buttons?: {
    /**
     * - Used for accessibility and key in map()
     */
    name: string;
    icon: SVGComponent;
    /**
     * - Optional onPress to allow also menu buttons
     */
    onPress?: () => void;
    asMenu?: boolean;
    menuItems?: (onPress: () => void) => ReactNode;
    disabled?: boolean;
    /**
     * Note: for icon button the colors will be used as background color
     */
    enabledColor?: string;
    disabledColor?: string;
    /**
     * button will not wrapped inside Appbar.Action if true
     * Note: passed colors will not be applied as no props of the passed icon button are changed here, but are passed as SVGIconProps
     */
    noAppBarActionWrap?: boolean;
    badge?: string | number;
    badgeColor?: string;
  }[];
  /**
   * When true (default), empty placeholder actions are rendered on the start side
   * to visually balance the end buttons (useful for centered titles).
   * Set to false to suppress these non-interactive placeholders.
   */
  balanceStartButtons?: boolean;
};

const style = StyleSheet.create({
  badge: {
    position: 'absolute',
    marginTop: 6,
    marginRight: 6
  }
});

const PROFILE_FALLBACK_ROUTES = new Set<string>([
  AppRoute.ProfileEdit,
  AppRoute.Chats,
  AppRoute.MyPosts,
  AppRoute.Participated,
  AppRoute.AppSetting,
  AppRoute.NotificationSetting,
  AppRoute.OrganizationOverview,
  AppRoute.DeleteAccount,
  AppRoute.Legal
]);

export const NavigationBar: FC<NavigationBarProps> = ({
  type = NavBarType.DEFAULT,
  direction = 'row',
  title = '',
  titleComponent,
  titleVariant = 'titleLarge',
  titleColor,
  subTitle,
  subTitleVariant = 'bodyMedium',
  subTitleColor,
  centerTitle,
  onTitlePress,
  iconColor,
  backgroundColor,
  hasBackButton = true,
  disableBackButton,
  onBackButtonPress,
  picture,
  pictureFallback,
  onPicturePress,
  buttons: endButtons,
  balanceStartButtons = true
}) => {
  const navigation = useAppNavigation();
  const { colors } = useAppTheme();

  const defaultColor = useMemo(
    () =>
      type === NavBarType.MODAL_WITH_BACK ||
      type === NavBarType.MODAL_WITH_CLOSE
        ? colors.onSurfaceVariant
        : colors.onSurface,
    [colors.onSurface, colors.onSurfaceVariant, type]
  );

  const iconProps = useMemo(
    () => ({
      fill: iconColor ?? colors.primary,
      width: 24,
      height: 24
    }),
    [iconColor, colors.primary]
  );

  const handleBackButtonPress = useCallback(() => {
    if (onBackButtonPress) {
      onBackButtonPress();
    } else {
      if (navigation.canGoBack()) {
        navigation.dispatch(CommonActions.goBack());
      } else {
        const currentRoute = navigation.getState().routes.at(-1)?.name;
        if (currentRoute && PROFILE_FALLBACK_ROUTES.has(currentRoute)) {
          navigation.dispatch(
            CommonActions.navigate(AppRoute.Tabs, {
              screen: TabRoute.Profile,
              pop: true
            })
          );
          return;
        }
        navigation.dispatch(
          CommonActions.navigate(AppRoute.Tabs, { pop: true })
        );
      }
    }
  }, [navigation, onBackButtonPress]);

  useFocusEffect(
    useCallback(() => {
      if (onBackButtonPress) {
        const subscription = BackHandler.addEventListener(
          'hardwareBackPress',
          () => {
            onBackButtonPress();
            return true;
          }
        );

        return () => subscription.remove();
      }
    }, [onBackButtonPress])
  );

  const titleContent = useMemo(
    () => (
      <Appbar.Content
        title={
          titleComponent ?? (
            <NavigationBarDefaultTitle
              title={title}
              subTitle={subTitle}
              titleColor={titleColor ?? defaultColor}
              subTitleColor={subTitleColor ?? defaultColor}
              titleVariant={titleVariant}
              subTitleVariant={subTitleVariant}
            />
          )
        }
        onPress={onTitlePress}
        style={{ alignItems: centerTitle ? 'center' : 'flex-start' }}
      />
    ),
    [
      centerTitle,
      defaultColor,
      onTitlePress,
      subTitle,
      subTitleColor,
      subTitleVariant,
      title,
      titleColor,
      titleComponent,
      titleVariant
    ]
  );

  const backButton = useMemo(
    () =>
      hasBackButton && (
        <Appbar.Action
          tabIndex={0}
          disabled={disableBackButton}
          animated={false}
          icon={({ color }) =>
            type === NavBarType.MODAL_WITH_CLOSE ? (
              <CloseIcon
                {...iconProps}
                fill={disableBackButton ? color : iconProps.fill}
              />
            ) : (
              <ArrowBackIcon
                {...iconProps}
                fill={disableBackButton ? color : iconProps.fill}
              />
            )
          }
          onPress={handleBackButtonPress}
        />
      ),
    [disableBackButton, handleBackButtonPress, hasBackButton, iconProps, type]
  );

  const [menuVisible, setMenuVisible] = useState<Record<string, boolean>>();

  const buttons = useMemo(
    () =>
      hasItems(endButtons) &&
      endButtons.map(
        ({
          name,
          icon,
          disabled,
          onPress,
          asMenu,
          menuItems,
          enabledColor,
          disabledColor,
          noAppBarActionWrap,
          badge,
          badgeColor
        }) => {
          const iconColor = enabledColor ?? iconProps.fill;
          const iconDisabledColor =
            disabledColor ?? color(colors.background).alpha(0.3).string();
          if (asMenu) {
            return (
              <ExtendedMenu
                key={name}
                visible={menuVisible ? menuVisible[name] : false}
                onDismiss={() =>
                  setMenuVisible((prevValue) => ({
                    ...prevValue,
                    [name]: false
                  }))
                }
                anchor={
                  <Appbar.Action
                    key={name}
                    accessibilityHint={name}
                    accessibilityLabel={name}
                    disabled={disabled}
                    animated={false}
                    tabIndex={0}
                    icon={() =>
                      icon({
                        ...iconProps,
                        fill: disabled ? iconDisabledColor : iconColor
                      })
                    }
                    onPress={() => {
                      setMenuVisible((prevValue) => ({
                        ...prevValue,
                        [name]: true
                      }));
                    }}
                  />
                }
              >
                {menuItems?.(() =>
                  setMenuVisible((prevValue) => ({
                    ...prevValue,
                    [name]: false
                  }))
                )}
              </ExtendedMenu>
            );
          }

          if (noAppBarActionWrap) {
            return icon({
              ...iconProps,
              fill: disabled ? iconDisabledColor : iconColor
            });
          }

          if (!isEmptyOrNull(badge) && badge != 0) {
            return (
              <View key={name}>
                <Appbar.Action
                  accessibilityHint={name}
                  accessibilityLabel={name}
                  disabled={disabled}
                  animated={false}
                  tabIndex={0}
                  icon={() =>
                    icon({
                      ...iconProps,
                      fill: disabled ? iconDisabledColor : iconColor
                    })
                  }
                  onPress={onPress}
                />
                <Badge
                  style={style.badge}
                  theme={{ colors: { error: badgeColor ?? colors.error } }}
                >
                  {badge}
                </Badge>
              </View>
            );
          }

          return (
            <Appbar.Action
              key={name}
              accessibilityHint={name}
              accessibilityLabel={name}
              disabled={disabled}
              animated={false}
              tabIndex={0}
              icon={() =>
                icon({
                  ...iconProps,
                  fill: disabled ? iconDisabledColor : iconColor
                })
              }
              onPress={onPress}
            />
          );
        }
      ),
    [colors.background, colors.error, endButtons, iconProps, menuVisible]
  );

  const emptyStartButtons = useMemo(() => {
    if (!balanceStartButtons) return;

    let length = endButtons?.length ?? 0;

    if (hasBackButton) length = length - 1;
    if (picture || pictureFallback) length = length - 1;

    if (length < 1) return;

    return (
      <>
        {Array.from({ length }).map((_, index) => (
          <Appbar.Action key={index} icon={() => <></>} />
        ))}
      </>
    );
  }, [
    balanceStartButtons,
    endButtons?.length,
    hasBackButton,
    picture,
    pictureFallback
  ]);

  const handlePicturePress = useCallback(() => {
    const pictureUrl = getSizedMediaItemUrl(
      picture,
      MediaItemSize.ORIGINAL
    )?.url;

    if (isEmptyOrNull(pictureUrl)) return;
    navigation.navigate(AppRoute.Gallery, {
      mediaUrls: [pictureUrl]
    });
  }, [navigation, picture]);

  switch (type) {
    case NavBarType.MODAL_WITH_BACK:
    case NavBarType.MODAL_WITH_CLOSE: {
      return (
        <Appbar.Header
          mode={centerTitle ? 'center-aligned' : 'small'}
          style={{
            backgroundColor: backgroundColor ?? colors.surfaceContainerLow,
            flexDirection: direction
          }}
        >
          {type === NavBarType.MODAL_WITH_BACK && backButton}
          {type === NavBarType.MODAL_WITH_CLOSE && buttons}
          {titleContent}
          {type === NavBarType.MODAL_WITH_CLOSE && backButton}
          {type === NavBarType.MODAL_WITH_BACK && buttons}
        </Appbar.Header>
      );
    }
    default: {
      return (
        <Appbar.Header
          mode={centerTitle ? 'center-aligned' : 'small'}
          style={{
            backgroundColor: backgroundColor ?? colors.surfaceContainer,
            flexDirection: direction
          }}
        >
          {emptyStartButtons}
          {backButton}
          {(picture || pictureFallback) && (
            <Appbar.Action
              style={{ marginHorizontal: 0 }}
              animated={false}
              tabIndex={0}
              size={48}
              icon={() => (
                <ProfileAvatar
                  mediaItem={picture}
                  mediaSize={MediaItemSize.SMALL}
                  viewSize={48}
                  backgroundColor={colors.primary}
                  fallbackColor={colors.onPrimary}
                  localImageUri={pictureFallback?.localPicture}
                  textFallback={pictureFallback?.text}
                  iconFallback={pictureFallback?.icon}
                />
              )}
              onPress={
                onPicturePress ?? (picture ? handlePicturePress : undefined)
              }
            />
          )}
          {titleContent}

          {buttons}
        </Appbar.Header>
      );
    }
  }
};
